import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TInventoryRelocation } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination, TRecordAction } from '@/core/types'
import { useRouter } from 'vue-router'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TInventoryRelocation>({})
  const filters = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      created_at: [],
    },
    order_by: 'created_at,desc',
  })

  const locationsFilters = ref<ToQueryStringFilters>({
    warehouse_id: [],
    status_id: [1],
  })
  const warehouseFilters = ref<ToQueryStringFilters>({
    status_id: [1],
  })

  const router = useRouter()

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['id'],
  })

  const locationsQueryParams = useToQueryString(toReactive(locationsFilters))
  const warehouseQueryParams = useToQueryString(toReactive(warehouseFilters))

  const paginationModule = usePagination()

  const moduleQuery = useModuleQuery({
    currentPage: paginationModule.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
      locationsCatalog: locationsQueryParams.queryParams,
      warehouseCatalog: warehouseQueryParams.queryParams,
    },
    enableQuery: {
      loadRecord: false,
      warehouseCatalog: true,
      locationsCatalog: computed(
        () => !!(locationsQueryParams?.params.value.length > 1),
      ),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationModule.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at', 'id'],
  })

  const onAdd = async (payload: TInventoryRelocation) => {
    const order = await moduleQuery.addRecord.mutateAsync(payload)
    onRedirectToEdit(order)
  }

  const onEdit = async (payload: TInventoryRelocation) => {
    await moduleQuery.editRecord.mutateAsync(payload)
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onRedirectToEdit = (order: TInventoryRelocation) => {
    router.push({
      name: 'inventoryRelocationsDetail',
      params: {
        id: order.id,
      },
    })
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const warehouses = computed(() => moduleQuery.warehouseCatalog.data?.value)
  const model = computed(() => moduleQuery.loadRecord?.data?.value)
  const locations = computed(() => moduleQuery.locationsCatalog?.data?.value)

  const isLoading = computed(
    () =>
      moduleQuery.addRecord.isLoading.value ||
      moduleQuery.editRecord.isLoading.value ||
      moduleQuery.loadRecords.isLoading.value,
  )
  const isLoadingWarehouses = computed(
    () => moduleQuery.warehouseCatalog.isLoading.value,
  )
  const isLoadingLocations = computed(
    () => moduleQuery.locationsCatalog.isLoading.value,
  )

  return {
    model,
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    paginationModule: toReactive(paginationModule),
    isLoading,
    warehouses,
    isLoadingWarehouses,
    locations,
    isLoadingLocations,
    locationsFilters,
    onAction,
    onEdit,
    onAdd,
  }
})

export { useProvideModule, useModule }
